import { useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { Flame, LayoutDashboard, Lock, ShieldCheck, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { useScrollCompact } from "../../hooks/useScrollCompact";
import { triggerTabSwitchHapticIfNeeded } from "../../lib/native/haptics";

const TAB_PATHS = [
  { to: "/today", key: "today" as const, icon: LayoutDashboard, end: true },
  { to: "/habits", key: "habits" as const, icon: Flame, end: true },
  { to: "/insight", key: "insight" as const, icon: Sparkles, end: false },
  { to: "/rules", key: "rules" as const, icon: ShieldCheck, end: false },
] as const;

type LiquidGlassTabBarProps = {
  isPremium?: boolean;
};

export function LiquidGlassTabBar({ isPremium = true }: LiquidGlassTabBarProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const isCompact = useScrollCompact();
  const tabRefs = useRef(new Map<string, HTMLAnchorElement>());
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const tabs = TAB_PATHS.map((tab) => ({
    ...tab,
    label: t.navigation[tab.key],
  }));

  const activePath =
    tabs.find((tab) =>
      tab.end ? location.pathname === tab.to : location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`),
    )?.to ?? "/today";

  useLayoutEffect(() => {
    const activeTab = tabRefs.current.get(activePath);
    if (!activeTab) return;

    const updateIndicator = () => {
      setIndicator({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
        ready: true,
      });
    };

    updateIndicator();

    let frame = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateIndicator);
    };

    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(activeTab);

    window.addEventListener("resize", scheduleUpdate);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [activePath]);

  const scale = isCompact ? 0.94 : 1;

  return (
    <div
      className="liquid-glass-tabbar-host pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-[max(12px,env(safe-area-inset-bottom))]"
    >
      <motion.div
        className="pointer-events-auto flex w-full max-w-[680px] items-center"
        style={{ transformOrigin: "50% 100%" }}
        animate={{ scale }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { type: "spring", stiffness: 420, damping: 40, mass: 0.82 }
        }
      >
        <nav
          className="liquid-glass-pill relative flex min-h-[58px] flex-1 items-stretch rounded-full p-1.5"
          aria-label={t.common.primaryNavLabel}
        >
          {indicator.ready ? (
            <motion.div
              aria-hidden
              className="liquid-glass-indicator pointer-events-none absolute inset-y-1.5 rounded-full"
              initial={false}
              animate={{
                left: indicator.left,
                width: indicator.width,
              }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 520, damping: 38, mass: 0.75 }
              }
            />
          ) : null}

          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.to === activePath;
            const isLocked = !isPremium && tab.to !== "/today";

            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                onClick={() => triggerTabSwitchHapticIfNeeded(tab.to, activePath)}
                ref={(node) => {
                  if (node) tabRefs.current.set(tab.to, node);
                  else tabRefs.current.delete(tab.to);
                }}
                className="relative z-[1] flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1.5 py-1.5 text-[11px] font-semibold leading-none no-underline outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[oklch(72%_0.09_155)] focus-visible:ring-offset-2 focus-visible:ring-offset-[oklch(13%_0.015_165)]"
                aria-current={isActive ? "page" : undefined}
                aria-label={isLocked ? `${tab.label} (${t.navigation.premiumLocked})` : tab.label}
              >
                <span className="relative">
                  <Icon
                    size={20}
                    strokeWidth={isActive ? 2.25 : 1.9}
                    className={
                      isActive
                        ? "text-[oklch(88%_0.07_155)]"
                        : isLocked
                          ? "text-[oklch(52%_0.025_155)] opacity-75"
                          : "text-[oklch(58%_0.03_155)]"
                    }
                    aria-hidden
                  />
                  {isLocked && !isActive ? (
                    <Lock
                      size={9}
                      strokeWidth={2.5}
                      className="absolute -bottom-0.5 -right-1 text-[oklch(58%_0.04_155)] opacity-80"
                      aria-hidden
                    />
                  ) : null}
                </span>
                <span
                  className={
                    isActive
                      ? "text-[oklch(93%_0.04_155)]"
                      : isLocked
                        ? "text-[oklch(56%_0.02_155)] opacity-80"
                        : "text-[oklch(62%_0.025_155)]"
                  }
                >
                  {tab.label}
                </span>
              </NavLink>
            );
          })}
        </nav>
      </motion.div>
    </div>
  );
}
